import { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSupportChat } from '@/hooks/useSupportChat';
import { useAuth } from '@/hooks/useAuth';
import { useSupportThreadUnreadCounts } from '@/hooks/useSupportThreadUnreadCounts';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AttachmentPreview } from './SupportFileUpload';
import { makeLinksClickable } from '@/utils/linkUtils';
import { supabase } from '@/integrations/supabase/client';

interface SupportChatInterfaceProps {
  selectedThreadId?: string;
  onSelectThread: (threadId: string | undefined) => void;
  onClose: () => void;
}

export const SupportChatInterface = ({
  selectedThreadId,
  onSelectThread,
}: SupportChatInterfaceProps) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [newThreadSubject, setNewThreadSubject] = useState('');
  const [showNewThread, setShowNewThread] = useState(false);
  const [localSelectedId, setLocalSelectedId] = useState<string | undefined>(selectedThreadId);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { threads, messages, createThread, sendMessage, isSendingMessage, isCreatingThread } =
    useSupportChat(localSelectedId);
  
  const unreadCounts = useSupportThreadUnreadCounts();

  useEffect(() => {
    setLocalSelectedId(selectedThreadId);
  }, [selectedThreadId]);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark messages as read when viewing a thread
  useEffect(() => {
    if (!localSelectedId || !messages || !user?.id) return;

    const markMessagesAsRead = async () => {
      const unreadMessages = messages.filter(m => !m.read_at && m.user_id !== user.id);
      if (unreadMessages.length === 0) return;

      await supabase
        .from('support_messages')
        .update({ read_at: new Date().toISOString() })
        .in('id', unreadMessages.map(m => m.id));
    };

    markMessagesAsRead();
  }, [localSelectedId, messages, user?.id]);

  const handleSendMessage = () => {
    if (!message.trim() || !localSelectedId) return;

    sendMessage({ threadId: localSelectedId, message: message.trim() });
    setMessage('');
  };

  const handleCreateThread = () => {
    if (!newThreadSubject.trim()) return;

    createThread(newThreadSubject.trim());
    setNewThreadSubject('');
    setShowNewThread(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-500/10 text-blue-500';
      case 'in_progress':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'resolved':
        return 'bg-green-500/10 text-green-500';
      case 'closed':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Thread List View
  if (!localSelectedId) {
    // Sort threads: unread first, then by last_message_at
    const sortThreads = (threadList: typeof threads) => {
      return threadList?.sort((a, b) => {
        const aUnread = unreadCounts[a.id] || 0;
        const bUnread = unreadCounts[b.id] || 0;
        
        // Unread threads first
        if (aUnread > 0 && bUnread === 0) return -1;
        if (aUnread === 0 && bUnread > 0) return 1;
        
        // Then by date
        return new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime();
      });
    };

    const openThreads = sortThreads(threads?.filter((t) => t.status === 'open' || t.status === 'in_progress')) || [];
    const closedThreads = sortThreads(threads?.filter((t) => t.status === 'resolved' || t.status === 'closed')) || [];

    const ThreadList = ({ threads: threadList }: { threads: typeof threads }) => (
      <ScrollArea className="h-full pointer-events-auto">
        {threadList && threadList.length > 0 ? (
          <div className="p-3 space-y-2 pointer-events-auto">
            {threadList.map((thread) => {
              const unreadCount = unreadCounts[thread.id] || 0;
              const hasUnread = unreadCount > 0;
              
              return (
                <button
                  key={thread.id}
                  type="button"
                  onMouseDown={(e) => {
                    // Select on mouse down to avoid any click-cancellation from focus traps
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectThread(thread.id);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectThread(thread.id);
                  }}
                  className={`w-full text-left p-4 rounded-lg border transition-colors cursor-pointer relative pointer-events-auto ${
                    hasUnread ? 'bg-primary/5 border-primary/20 hover:bg-primary/10' : 'bg-card hover:bg-accent/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      {hasUnread && (
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                      )}
                      <h4 className={`text-sm flex-1 line-clamp-2 ${hasUnread ? 'font-semibold' : 'font-medium'}`}>
                        {thread.subject}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {hasUnread && (
                        <Badge variant="default" className="h-5 min-w-5 px-1.5 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                      <Badge variant="secondary" className={getStatusColor(thread.status)}>
                        {thread.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(thread.last_message_at), 'MMM d, h:mm a')}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-center p-6">
            <div>
              <p className="text-muted-foreground mb-4">No threads found</p>
            </div>
          </div>
        )}
      </ScrollArea>
    );

    return (
      <div className="flex flex-col h-full">
        {/* Header with grey background */}
        <div className="px-4 py-3 bg-muted/50 border-b flex items-center justify-between">
          <h2 className="font-semibold text-lg">Support Chat</h2>
          <button 
            onClick={() => setShowNewThread(!showNewThread)}
            className="flex items-center gap-1 text-sm font-semibold hover:text-primary transition-colors mr-10 sm:mr-12"
          >
            <Plus className="h-4 w-4" />
            New Thread
          </button>
        </div>

        {/* New Thread Form */}
        {showNewThread && (
          <div className="p-4 border-b bg-muted/30">
            <Input
              placeholder="What do you need help with?"
              value={newThreadSubject}
              onChange={(e) => setNewThreadSubject(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateThread()}
              disabled={isCreatingThread}
              className="mb-3"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleCreateThread}
                disabled={!newThreadSubject.trim() || isCreatingThread}
              >
                Create Thread
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowNewThread(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Tabs for Open/Closed */}
        <Tabs defaultValue="open" className="flex-1 flex flex-col min-h-0">
          <TabsList className="w-full rounded-none border-b bg-transparent p-0 h-auto shrink-0">
            <TabsTrigger 
              value="open" 
              className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
            >
              Open ({openThreads.length})
            </TabsTrigger>
            <TabsTrigger 
              value="closed"
              className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent py-3"
            >
              Closed ({closedThreads.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="open" className="flex-1 mt-0 min-h-0 data-[state=active]:flex data-[state=active]:flex-col">
            <ThreadList threads={openThreads} />
          </TabsContent>
          
          <TabsContent value="closed" className="flex-1 mt-0 min-h-0 data-[state=active]:flex data-[state=active]:flex-col">
            <ThreadList threads={closedThreads} />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Chat View
  const currentThread = threads?.find((t) => t.id === localSelectedId);

  return (
    <div className="flex flex-col h-full">
      {/* Header with grey background */}
      <div className="px-4 py-3 bg-muted/50 border-b">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => { setLocalSelectedId(undefined); onSelectThread(undefined); }}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base line-clamp-1">{currentThread?.subject}</h3>
            <Badge variant="secondary" className={`${getStatusColor(currentThread?.status || '')} mt-1`}>
              {currentThread?.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div className="p-4">
            {messages && messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isCurrentUser = msg.user_id === user?.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}
                      >
                        {!isCurrentUser && (
                          <p className="text-xs font-medium mb-1">
                            {msg.profiles?.full_name || msg.profiles?.email || 'Support Team'}
                          </p>
                        )}
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {makeLinksClickable(msg.message)}
                        </div>
                        
                        {/* Display attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {msg.attachments.map((attachment: any, idx: number) => (
                              <AttachmentPreview
                                key={idx}
                                attachment={attachment}
                                showRemove={false}
                              />
                            ))}
                          </div>
                        )}
                        
                        <p
                          className={`text-xs mt-1 ${
                            isCurrentUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}
                        >
                          {format(new Date(msg.created_at), 'h:mm a')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No messages yet. Start the conversation!
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      <Separator />

      {/* Input */}
      <div className="p-4 shrink-0">
        <div className="flex gap-2">
          <Input
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
            disabled={isSendingMessage}
          />
          <Button onClick={handleSendMessage} disabled={!message.trim() || isSendingMessage}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
